// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
import { VendorFormModal } from '~/components/module/vendors';
// Others
import { IPaginationResponse } from '~/utils/interface/base';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_VENDOR,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IBaseMoreAction, IHeaderCellType, ISortBy } from '~/utils/interface/common';
import { IQueryParamVendors, IVendors } from '~/utils/interface/vendors';
import { formatPhoneNumber } from '~/utils/helper/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { deleteVendor, getListVendors } from '~/thunks/vendors/vendorsThunk';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { INVOICE_PERMISSIONS } from '~/utils/constants/permissions';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './Vendors.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string, record: IVendors) => void,
  onEdit: (record: IVendors) => void,
  onDelete: (record: IVendors) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IVendors>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => <Box className={cx('textName')}>{record.vendorName}</Box>,
    },
    {
      id: 'email',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_email')}</Box>
          <SortComponent name='email' sortType={getSortType('email')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => <Box className={cx('textName')}>{record.email || EMPTY_VALUE}</Box>,
    },
    {
      id: 'phone',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_phone')}</Box>
          <SortComponent name='phone' sortType={getSortType('phone')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => (
        <Box className={cx('textName')}>{record.phoneNumber ? formatPhoneNumber(record.phoneNumber) : EMPTY_VALUE}</Box>
      ),
    },
    {
      id: 'contactName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_contact_name')}</Box>
          <SortComponent name='contact name' sortType={getSortType('contact name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => <Box className={cx('textName')}>{record.contactName || EMPTY_VALUE}</Box>,
    },
    {
      id: 'address',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_address')}</Box>
          <SortComponent name='address' sortType={getSortType('address')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => <Box className={cx('textName')}>{record.address || EMPTY_VALUE}</Box>,
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_type')}</Box>
          <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IVendors) => <Box className={cx('textName')}>{record.type || EMPTY_VALUE}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IVendors) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id, record),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Vendors = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateInvoices, canViewInvoices, canDetailInvoices, canEditInvoices, canDeleteInvoices } =
    usePermissions(INVOICE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/vendors']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isRefreshList } = useAppSelector((state) => state.vendor);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(() => String(params?.textSearch || EMPTY_STRING), [params?.textSearch]);

  const sortBy = useMemo<ISortBy[]>(() => {
    return DEFAULT_SORT_VENDOR.map((item) => ({
      ...item,
      typeSort: item.name === params?.sortBy ? params?.sortType : undefined,
    }));
  }, [params?.sortBy, params?.sortType]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [vendorsList, setVendorsList] = useState<IVendors[]>([]);
  const [vendorsSelected, setVendorSelected] = useState<IVendors>();
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalItems, setTotalItems] = useState<number>(0);
  const [idsInventory, setIdsInventory] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<IVendors[]>([]);
  const [searchKey, setSearchKey] = useState<string>(textSearchParams || EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey.trim(), DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormVendor, setIsShowFormVendor] = useState<boolean>(false);
  const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!isRefreshList) return;
    const newParams: IQueryParamVendors = {
      page: pageSelected,
      limit: limitPage,
      ...(params?.sortBy
        ? {
            sortBy: params?.sortBy,
          }
        : {}),
      ...(params?.sortType
        ? {
            sortType: params?.sortType,
          }
        : {}),
      ...(params?.textSearch
        ? {
            textSearch: params?.textSearch,
          }
        : {}),
    };

    handleGetListVendors(newParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshList]);

  useEffect(() => {
    if (!textSearchParams) setSearchKey(EMPTY_STRING);
  }, [textSearchParams]);

  useEffect(() => {
    const newParams: IQueryParamVendors = {
      page: pageSelected,
      limit: limitPage,
      ...(params?.sortBy
        ? {
            sortBy: params?.sortBy,
          }
        : {}),
      ...(params?.sortType
        ? {
            sortType: params?.sortType,
          }
        : {}),
      ...(params?.textSearch
        ? {
            textSearch: params?.textSearch,
          }
        : {}),
    };

    handleGetListVendors(newParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!pagination) return;

    if (debounceSearchKey) {
      setSearchParams({
        ...params,
        page: DEFAULT_CURRENT_PAGE.toString(),
        limit: DEFAULT_LIMIT_PAGE.toString(),
        textSearch: debounceSearchKey,
      });
    } else {
      const { textSearch, ...rest } = params;
      setSearchParams(rest);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListVendors = (params: IQueryParamVendors) => {
    setIsLoading(true);
    dispatch(getListVendors(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;
        if (!responses) return;

        setVendorsList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleAddVendor = () => {
    setIsShowFormVendor(true);
  };

  const handleCloseVendorForm = () => {
    setIsShowFormVendor(false);
    setVendorSelected(undefined);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: IVendors[]) => {
    setIdsInventory(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSort = (sortType: string, name: string) => {
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handleView = (id: string, record: IVendors) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.vendors}/${id}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.vendors}/${id}`);
        break;
    }
  };

  const handleEdit = (record: IVendors) => {
    setIsShowFormVendor(true);
    setVendorSelected(record);
  };

  const handleShowConfirmDelete = (record: IVendors) => {
    setIsOpenConfirmDelete(true);
    setVendorSelected(record);
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirmDelete(false);
    setVendorSelected(undefined);
  };

  const handleConfirmDelete = () => {
    if (!vendorsSelected?.id) return;

    setIsLoading(true);

    dispatch(deleteVendor(vendorsSelected?.id))
      .unwrap()
      .then(() => {
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box id='vendorsPage' className={cx('container')}>
      <BaseToolbar
        title={t('vendors_title_vendor_page')}
        total={t('vendors_page_total_vendor', { total: canViewInvoices ? totalItems : 0 })}
      >
        <BaseInputSearch
          width={200}
          value={searchKey}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', {
            ns: 'common/base',
          })}
        />

        {canCreateInvoices && (
          <Button
            type='button'
            variant='outlined'
            className={cx('button')}
            onClick={handleAddVendor}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
          >
            {t('vendor_page_add_vendor')}
          </Button>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewInvoices ? vendorsList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleShowConfirmDelete,
            canDetailInvoices,
            canEditInvoices,
            canDeleteInvoices
          )}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsInventory}
          // onSelectChange={handleRowsSelect}
          {...(canDetailInvoices ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowFormVendor && <VendorFormModal onClose={handleCloseVendorForm} vendorId={vendorsSelected?.id} />}

      {isOpenConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('confirm_delete_vendor')}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
};

export default Vendors;
